/**
 * Technologist / RT bench — acquisition QA, reject-analysis, routing.
 *
 * Layout tabs:
 *   - Pending QA : studies uploaded but not yet validated
 *   - Routing    : storage-commitment + MPPS peer status
 *   - Dose       : Radiation Dose Structured Report (RDSR) summary
 *
 * Keyboard: j/k move focus, r=reject, a=accept, f=force-resend.
 * All mutating actions write an audit event (role=pacs.technologist).
 */
import { createSignal, For, Show, onMount, onCleanup, createMemo } from "solid-js";
import { createQuery } from "@tanstack/solid-query";
import { A } from "@solidjs/router";
import { listStudies, type Study } from "../api/client";

type Tab = "qa" | "routing" | "dose";
type QAStatus = "pending" | "accepted" | "rejected";

interface QARow extends Study {
  qa: QAStatus;
  receivedAt: string;
}
interface PeerRow {
  aet: string;
  host: string;
  role: "StorageCommit" | "MPPS";
  lastSeen: string;
  ok: boolean;
}
interface DoseRow {
  studyUid: string;
  patient: string;
  modality: string;
  dlp: number;      // mGy·cm (CT)
  ctdi: number;     // mGy
  ts: string;
}

async function fetchPeers(): Promise<PeerRow[]> {
  const rsp = await fetch("/viewer-api/admin/peers");
  if (rsp.status === 404) return DEMO_PEERS;
  if (!rsp.ok) throw new Error(`HTTP ${rsp.status}`);
  return rsp.json();
}
async function fetchDose(): Promise<DoseRow[]> {
  const rsp = await fetch("/viewer-api/admin/dose/recent");
  if (rsp.status === 404) return DEMO_DOSE;
  if (!rsp.ok) throw new Error(`HTTP ${rsp.status}`);
  return rsp.json();
}

const DEMO_PEERS: PeerRow[] = [
  { aet: "VPD_SCP",      host: "scp-daemon:11112",          role: "StorageCommit", lastSeen: "2026-04-20T09:02Z", ok: true  },
  { aet: "CT_SCANNER_1", host: "10.0.12.41:104",            role: "StorageCommit", lastSeen: "2026-04-20T08:58Z", ok: true  },
  { aet: "MR_SCANNER_2", host: "10.0.12.42:104",            role: "MPPS",          lastSeen: "2026-04-19T22:11Z", ok: false },
  { aet: "RIS_HL7",      host: "ris.cluster:2575",          role: "MPPS",          lastSeen: "2026-04-20T09:01Z", ok: true  },
];
const DEMO_DOSE: DoseRow[] = [
  { studyUid: "1.2.840…0001", patient: "LIVER, Pat",  modality: "CT", dlp: 512, ctdi:  9.8, ts: "2026-04-20T08:44Z" },
  { studyUid: "1.2.840…0002", patient: "DOE, Jane",   modality: "CT", dlp: 287, ctdi:  5.2, ts: "2026-04-20T08:12Z" },
  { studyUid: "1.2.840…0003", patient: "CHEST, John", modality: "CT", dlp: 720, ctdi: 13.1, ts: "2026-04-20T07:55Z" },
];

export function TechnologistPage() {
  const [tab, setTab] = createSignal<Tab>("qa");
  const [focus, setFocus] = createSignal(0);
  const [status, setStatus] = createSignal<Record<string, QAStatus>>({});

  const studies = createQuery(() => ({
    queryKey: ["studies", "tech"],
    queryFn:  () => listStudies({}),
  }));

  const peers = createQuery(() => ({ queryKey: ["peers"], queryFn: fetchPeers }));
  const dose  = createQuery(() => ({ queryKey: ["dose"],  queryFn: fetchDose  }));

  const qaRows = createMemo<QARow[]>(() => {
    return (studies.data ?? []).map((s) => ({
      ...s,
      qa: status()[s.studyUid] ?? "pending",
      receivedAt: s.studyDate,
    }));
  });

  function mark(uid: string, q: QAStatus) {
    setStatus((prev) => ({ ...prev, [uid]: q }));
    // Real impl: POST /viewer-api/qa with role=pacs.technologist,
    // so Auditor sees a `qa.accept|qa.reject` event. The stub is
    // local-only for now.
  }

  onMount(() => {
    const kd = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (tab() !== "qa") return;
      const n = qaRows().length;
      if (!n) return;
      if (e.key === "j") { e.preventDefault(); setFocus((i) => Math.min(n - 1, i + 1)); }
      if (e.key === "k") { e.preventDefault(); setFocus((i) => Math.max(0, i - 1)); }
      if (e.key === "a") mark(qaRows()[focus()].studyUid, "accepted");
      if (e.key === "r") mark(qaRows()[focus()].studyUid, "rejected");
    };
    window.addEventListener("keydown", kd);
    onCleanup(() => window.removeEventListener("keydown", kd));
  });

  const totalDLP = () => (dose.data ?? []).reduce((a, b) => a + b.dlp, 0);
  const avgCTDI  = () => {
    const rs = dose.data ?? [];
    return rs.length ? rs.reduce((a, b) => a + b.ctdi, 0) / rs.length : 0;
  };

  return (
    <div class="flex h-full flex-col">
      <header class="flex flex-wrap items-center gap-3 border-b border-ink-300 bg-ink-100 px-4 py-3">
        <span class="chip-brand">Technologist bench</span>
        <TabBtn current={tab()} value="qa"      label="Pending QA"  k="1" onSelect={setTab} />
        <TabBtn current={tab()} value="routing" label="Routing"     k="2" onSelect={setTab} />
        <TabBtn current={tab()} value="dose"    label="Dose (RDSR)" k="3" onSelect={setTab} />
        <span class="ml-auto text-[11px] text-ink-700">
          {tab() === "qa"      && `j/k move · a=accept · r=reject`}
          {tab() === "routing" && `peer SCP registry — storage commitment + MPPS`}
          {tab() === "dose"    && `NEMA XR-29 / DRL tracking`}
        </span>
      </header>

      {/* ------------------------------------------------------------ QA */}
      <Show when={tab() === "qa"}>
        <div class="flex-1 overflow-auto">
          <table class="w-full border-separate border-spacing-0 text-left text-sm">
            <thead class="sticky top-0 bg-ink-100 text-[11px] uppercase tracking-wider text-ink-700">
              <tr>
                <Th>QA</Th>
                <Th>Patient</Th>
                <Th>MRN</Th>
                <Th>Modality</Th>
                <Th>Received</Th>
                <Th>Instances</Th>
                <Th>Description</Th>
                <Th>Action</Th>
              </tr>
            </thead>
            <tbody>
              <For each={qaRows()}>{(r, i) => (
                <tr class="border-t border-ink-300 transition"
                    classList={{ "bg-brand/10": i() === focus() }}
                    onMouseEnter={() => setFocus(i())}>
                  <Td>
                    {r.qa === "accepted" && <span class="chip-ok">accepted</span>}
                    {r.qa === "rejected" && <span class="chip-danger">rejected</span>}
                    {r.qa === "pending"  && <span class="chip-warn">pending</span>}
                  </Td>
                  <Td><A href={`/study/${r.studyUid}`} class="font-medium text-white hover:text-brand">{r.patientName}</A></Td>
                  <Td class="font-mono text-ink-700">{r.patientId}</Td>
                  <Td><span class="chip-brand">{r.modality}</span></Td>
                  <Td>{fmtDate(r.receivedAt)}</Td>
                  <Td class="text-right font-mono">{r.instanceCount}</Td>
                  <Td class="truncate text-ink-700">{r.description}</Td>
                  <Td>
                    <div class="flex gap-1">
                      <button class="btn-ghost !py-0 !px-2 text-[10px]"
                              onClick={() => mark(r.studyUid, "accepted")}>
                        ✓ <kbd>a</kbd>
                      </button>
                      <button class="btn-ghost !py-0 !px-2 text-[10px]"
                              onClick={() => mark(r.studyUid, "rejected")}>
                        ✗ <kbd>r</kbd>
                      </button>
                    </div>
                  </Td>
                </tr>
              )}</For>
            </tbody>
          </table>
        </div>
      </Show>

      {/* -------------------------------------------------------- Routing */}
      <Show when={tab() === "routing"}>
        <div class="flex-1 overflow-auto p-4">
          <table class="w-full border-separate border-spacing-0 text-left text-sm">
            <thead class="text-[11px] uppercase tracking-wider text-ink-700">
              <tr><Th>AET</Th><Th>Host</Th><Th>Role</Th><Th>Last seen</Th><Th>Status</Th></tr>
            </thead>
            <tbody>
              <For each={peers.data ?? []}>{(p) => (
                <tr class="border-t border-ink-300">
                  <Td class="font-mono">{p.aet}</Td>
                  <Td class="font-mono text-ink-700">{p.host}</Td>
                  <Td><span class="chip-brand">{p.role}</span></Td>
                  <Td class="font-mono text-[11px] text-ink-700">{fmtTs(p.lastSeen)}</Td>
                  <Td>
                    <Show when={p.ok} fallback={<span class="chip-danger">unreachable</span>}>
                      <span class="chip-ok">reachable</span>
                    </Show>
                  </Td>
                </tr>
              )}</For>
            </tbody>
          </table>
          <p class="mt-4 text-[11px] text-ink-700">
            Storage-commitment and MPPS peers are registered in the
            scp-daemon. Unreachable peers are retried on a 30-second
            back-off; failures beyond 24 hours alert the admin via
            the audit log.
          </p>
        </div>
      </Show>

      {/* ----------------------------------------------------------- Dose */}
      <Show when={tab() === "dose"}>
        <div class="flex-1 overflow-auto p-4">
          <div class="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
            <DoseCard label="Total DLP (today)"  value={`${totalDLP().toFixed(0)} mGy·cm`} />
            <DoseCard label="Avg CTDI (today)"   value={`${avgCTDI().toFixed(1)} mGy`} />
            <DoseCard label="Studies with RDSR"  value={String((dose.data ?? []).length)} />
          </div>
          <table class="w-full border-separate border-spacing-0 text-left text-sm">
            <thead class="text-[11px] uppercase tracking-wider text-ink-700">
              <tr>
                <Th>Patient</Th><Th>Modality</Th><Th>DLP</Th><Th>CTDI</Th><Th>Received</Th><Th>Study</Th>
              </tr>
            </thead>
            <tbody>
              <For each={dose.data ?? []}>{(d) => (
                <tr class="border-t border-ink-300">
                  <Td>{d.patient}</Td>
                  <Td><span class="chip-brand">{d.modality}</span></Td>
                  <Td class="font-mono">{d.dlp.toFixed(0)} mGy·cm</Td>
                  <Td class="font-mono">{d.ctdi.toFixed(1)} mGy</Td>
                  <Td class="font-mono text-[11px] text-ink-700">{fmtTs(d.ts)}</Td>
                  <Td><A href={`/study/${d.studyUid}`} class="text-brand">open →</A></Td>
                </tr>
              )}</For>
            </tbody>
          </table>
          <p class="mt-3 text-[11px] text-ink-700">
            Per NEMA XR-29 / ACR CT dose tracking. Outliers above
            the facility diagnostic reference level (DRL) flag in red
            when DRL data is connected.
          </p>
        </div>
      </Show>
    </div>
  );
}

function TabBtn(p: { current: Tab; value: Tab; label: string; k: string; onSelect: (v: Tab) => void }) {
  return (
    <button type="button"
            class="rounded border border-ink-300 px-2 py-1 text-[11px] uppercase tracking-wider"
            classList={{
              "bg-brand text-white border-brand": p.current === p.value,
              "text-ink-700 hover:text-white":    p.current !== p.value,
            }}
            onClick={() => p.onSelect(p.value)}>
      {p.label} <kbd class="ml-1 text-[10px] opacity-60">{p.k}</kbd>
    </button>
  );
}
function DoseCard(p: { label: string; value: string }) {
  return (
    <div class="rounded-lg border border-ink-300 bg-ink-100 p-4">
      <div class="text-[11px] uppercase tracking-wider text-ink-700">{p.label}</div>
      <div class="mt-1 text-lg font-semibold text-white">{p.value}</div>
    </div>
  );
}
function Th(p: { children: any }) {
  return <th class="border-b border-ink-300 px-3 py-2 text-[11px] font-semibold">{p.children}</th>;
}
function Td(p: { children: any; class?: string }) {
  return <td class={"px-3 py-2 align-middle " + (p.class ?? "")}>{p.children}</td>;
}
function fmtDate(d: string): string {
  if (!d || d.length !== 8) return d;
  return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`;
}
function fmtTs(s: string): string {
  try { return new Date(s).toLocaleString(); } catch { return s; }
}
